import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import TopNavigation from '../../components/ui/TopNavigation';
import { CampaignManagementService } from '../../services/campaignManagementService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';

import QuickExportMenu from '../../components/ui/QuickExportMenu';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CampaignFilters from './components/CampaignFilters';
import CampaignPerformanceTable from './components/CampaignPerformanceTable';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import CampaignTimeline from './components/CampaignTimeline';
import ABTestingWidget from './components/ABTestingWidget';
import CreateCampaignModal from './components/CreateCampaignModal';
import CampaignEditModal from './components/CampaignEditModal';
import { useLocation } from 'react-router-dom';

const CampaignManagementHub = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('last30d');
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Data state
  const [campaigns, setCampaigns] = useState([]);
  const [campaignSummary, setCampaignSummary] = useState(null);
  const [timelineData, setTimelineData] = useState([]);
  const [abTestData, setAbTestData] = useState([]);
  const [error, setError] = useState('');

  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  // Handle URL parameters for editing
  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const editCampaignId = searchParams?.get('edit');
    
    if (editCampaignId && campaigns?.length > 0) {
      const campaignToEdit = campaigns?.find(c => c?.id === editCampaignId);
      if (campaignToEdit) {
        setEditingCampaign(campaignToEdit);
        setShowEditModal(true);
      }
    }
  }, [location?.search, campaigns]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Load campaign data
  const loadCampaignData = async () => {
    if (!user) return;

    setIsLoading(true);
    setError('');

    try {
      // Load campaigns with filters
      const filters = {
        status: statusFilter,
        search: searchTerm,
        sortBy,
        sortOrder,
        dateRange: selectedDateRange
      };

      const [
        campaignsResult,
        summaryResult,
        timelineResult,
        abTestResult
      ] = await Promise.all([
        CampaignManagementService?.getCampaigns(filters),
        CampaignManagementService?.getCampaignAnalyticsSummary(selectedDateRange),
        CampaignManagementService?.getCampaignTimeline(20),
        CampaignManagementService?.getABTestingData()
      ]);

      if (campaignsResult?.error) {
        setError(`Campaigns: ${campaignsResult?.error}`);
      } else {
        setCampaigns(campaignsResult?.data || []);
      }

      if (summaryResult?.error) {
        console.log('Summary error:', summaryResult?.error);
      } else {
        setCampaignSummary(summaryResult?.data);
      }

      if (timelineResult?.error) {
        console.log('Timeline error:', timelineResult?.error);
      } else {
        setTimelineData(timelineResult?.data || []);
      }

      if (abTestResult?.error) {
        console.log('A/B Test error:', abTestResult?.error);
      } else {
        setAbTestData(abTestResult?.data || []);
      }

      // If there are errors but we got some data, show a warning instead of full error
      if ((campaignsResult?.error || summaryResult?.error) && campaigns?.length === 0) {
        setError('Some data could not be loaded. Please check your connection.');
      }

    } catch (error) {
      setError(`Failed to load campaign data: ${error?.message}`);
      setCampaigns([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Load data when filters change
  useEffect(() => {
    loadCampaignData();
  }, [user, selectedDateRange, statusFilter, searchTerm, sortBy, sortOrder]);

  const handleCampaignCreated = async (newCampaign) => {
    // Auto-refresh data after campaign creation
    await loadCampaignData();
    // Close modal after successful refresh
    setShowCreateModal(false);
  };

  const handleCampaignUpdated = async (updatedCampaign) => {
    // Update campaigns list locally
    setCampaigns(prev => prev?.map(campaign => 
      campaign?.id === updatedCampaign?.id ? { ...campaign, ...updatedCampaign } : campaign
    ));
    
    // Refresh all data to get updated analytics
    await loadCampaignData();
    
    // Close edit modal
    setShowEditModal(false);
    setEditingCampaign(null);
  };

  const handleCampaignSelect = (campaignId, isSelected) => {
    setSelectedCampaigns(prev => 
      isSelected 
        ? [...prev, campaignId]
        : prev?.filter(id => id !== campaignId)
    );
  };

  // Enhanced bulk action handler with status updates
  const handleBulkAction = async (action) => {
    if (selectedCampaigns?.length === 0) return;

    try {
      setIsLoading(true);
      
      let updates = {};
      switch (action) {
        case 'activate':
          updates = { status: 'active' };
          break;
        case 'pause':
          updates = { status: 'paused' };
          break;
        case 'archive':
          updates = { status: 'archived' };
          break;
        case 'complete':
          updates = { status: 'completed' };
          break;
        case 'draft':
          updates = { status: 'draft' };
          break;
        case 'delete':
          // Handle delete separately
          for (const campaignId of selectedCampaigns) {
            await CampaignManagementService?.deleteCampaign(campaignId);
          }
          setSelectedCampaigns([]);
          loadCampaignData();
          return;
        default:
          return;
      }

      const { error } = await CampaignManagementService?.bulkUpdateCampaigns(selectedCampaigns, updates);
      
      if (error) {
        setError(`Bulk ${action} failed: ${error}`);
      } else {
        setSelectedCampaigns([]);
        loadCampaignData();
      }
    } catch (error) {
      setError(`Bulk ${action} failed: ${error?.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Prepare export data
  const getExportData = () => {
    return campaigns?.map(campaign => ({
      name: campaign?.name,
      status: campaign?.status,
      totalScans: campaign?.analytics?.totalScans || 0,
      totalConversions: campaign?.analytics?.totalConversions || 0,
      conversionRate: campaign?.analytics?.conversionRate || 0,
      activeQRCodes: campaign?.analytics?.activeQRCodes || 0,
      performanceScore: campaign?.analytics?.performanceScore || 0,
      createdAt: campaign?.createdAt?.toISOString()?.split('T')?.[0] || '',
      targetScans: campaign?.targetScans || 0
    })) || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation />
      
      {/* Main content now properly positioned underneath top navigation */}
      <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Campaign Management Hub</h1>
                <p className="text-gray-600 mt-2">
                  Manage and track the performance of your QR code campaigns
                </p>
              </div>
              <div className="mt-4 lg:mt-0">
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="inline-flex items-center gap-2"
                >
                  <Icon name="Plus" size={16} />
                  New Campaign
                </Button>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-start">
                <Icon name="AlertTriangle" size={20} className="text-yellow-600 mt-0.5 mr-3" />
                <div>
                  <p className="font-medium text-yellow-800">Data Loading Issue</p>
                  <p className="text-sm text-yellow-700 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          {campaignSummary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Icon name="Target" size={20} className="text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Campaigns</p>
                    <p className="text-2xl font-semibold text-gray-900">{campaignSummary?.totalCampaigns || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Icon name="Play" size={20} className="text-green-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Active Campaigns</p>
                    <p className="text-2xl font-semibold text-gray-900">{campaignSummary?.activeCampaigns || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Icon name="BarChart3" size={20} className="text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Total Scans</p>
                    <p className="text-2xl font-semibold text-gray-900">{campaignSummary?.totalScans?.toLocaleString() || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <Icon name="TrendingUp" size={20} className="text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Avg Conversion</p>
                    <p className="text-2xl font-semibold text-gray-900">{campaignSummary?.avgConversionRate || 0}%</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Bulk Actions */}
          <div className="mb-6 space-y-4">
            <CampaignFilters
              statusFilter={statusFilter}
              onStatusChange={setStatusFilter}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              sortBy={sortBy}
              onSortByChange={setSortBy}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              onFiltersChange={() => {}}
            />

            {selectedCampaigns?.length > 0 && (
              <BulkOperationsPanel
                selectedCount={selectedCampaigns?.length}
                onBulkAction={handleBulkAction}
                onClearSelection={() => setSelectedCampaigns([])}
                isLoading={isLoading}
                onOperationComplete={() => {}}
              />
            )}
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-8">
            {/* Campaign Performance Table */}
            <div className="xl:col-span-3">
              <CampaignPerformanceTable
                campaigns={campaigns}
                isLoading={isLoading}
                selectedCampaigns={selectedCampaigns}
                onCampaignSelect={handleCampaignSelect}
                onCampaignEdit={(campaign) => {
                  setEditingCampaign(campaign);
                  setShowEditModal(true);
                }}
                onCampaignStatusToggle={async (campaignId, newStatus) => {
                  try {
                    setIsLoading(true);
                    const { error } = await CampaignManagementService?.updateCampaign(campaignId, { status: newStatus });
                    if (error) {
                      setError(`Failed to ${newStatus === 'active' ? 'activate' : 'deactivate'} campaign: ${error}`);
                    } else {
                      loadCampaignData();
                    }
                  } catch (error) {
                    setError(`Failed to update campaign status: ${error?.message}`);
                  } finally {
                    setIsLoading(false);
                  }
                }}
                sortBy={sortBy}
                sortOrder={sortOrder}
                comparisonMode={false}
                onSort={(field) => {
                  if (sortBy === field) {
                    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                  } else {
                    setSortBy(field);
                    setSortOrder('desc');
                  }
                }}
              />
            </div>

            {/* Sidebar Widgets */}
            <div className="xl:col-span-1 space-y-6">
              {/* A/B Testing Widget */}
              <ABTestingWidget
                abTests={abTestData}
                isLoading={isLoading}
              />

              {/* Campaign Timeline */}
              <CampaignTimeline
                timelineData={timelineData}
                isLoading={isLoading}
              />
            </div>
          </div>
        </div>
      </main>

      <QuickExportMenu onDataReady={getExportData} />

      {/* Create Campaign Modal */}
      <CreateCampaignModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCampaignCreated={handleCampaignCreated}
      />

      {/* Edit Campaign Modal */}
      <CampaignEditModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingCampaign(null);
        }}
        campaign={editingCampaign}
        onCampaignUpdated={handleCampaignUpdated}
      />
    </div>
  );
};

export default CampaignManagementHub;